/** Keystone timer, +2/+3 thresholds, forces bar, deaths and boss checklist. */
import type { ContentRegistry, DeepReadonly, World } from '@keystone/sim';
import { el, pct, setClass, setStyle, setText } from './dom';
import { clock } from './format';

/** Chest thresholds as fractions of the timer (retail: +3 at 60%, +2 at 80%). */
export const CHEST_THRESHOLDS = { plus3: 0.6, plus2: 0.8 } as const;

export class KeystonePanel {
  readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly time: HTMLElement;
  private readonly thresholds: HTMLElement;
  private readonly timeBar: HTMLElement;
  private readonly forcesFill: HTMLElement;
  private readonly forcesText: HTMLElement;
  private readonly deaths: HTMLElement;
  private readonly bosses: HTMLElement;
  private readonly hint: HTMLElement;

  constructor(parent: HTMLElement) {
    this.root = el('div', 'panel keystone', parent);
    this.title = el('div', 'ks-title', this.root);
    this.time = el('div', 'ks-time', this.root);
    const tb = el('div', 'ks-timebar', this.root);
    this.timeBar = el('div', 'ks-timebar-fill', tb);
    el('div', 'ks-mark', tb).style.left = pct(CHEST_THRESHOLDS.plus3);
    el('div', 'ks-mark', tb).style.left = pct(CHEST_THRESHOLDS.plus2);
    this.thresholds = el('div', 'ks-thresholds', this.root);
    this.bosses = el('div', 'ks-bosses', this.root);
    const fb = el('div', 'ks-forces', this.root);
    this.forcesFill = el('div', 'ks-forces-fill', fb);
    this.forcesText = el('div', 'ks-forces-text', fb);
    this.deaths = el('div', 'ks-deaths', this.root);
    this.hint = el('div', 'ks-hint', this.root);
  }

  update(world: DeepReadonly<World>, content: ContentRegistry): void {
    const m = world.mplus;
    const d = content.dungeon(world.dungeonId);
    setText(this.title, `${d?.name ?? world.dungeonId}  +${m.level}${m.affixes.length ? `  • ${m.affixes.join(', ')}` : ''}`);
    const timer = Math.max(1, m.timerMs);
    const elapsed = m.elapsedMs;
    setText(this.time, `${clock(elapsed)} / ${clock(timer)}`);
    setClass(this.time, 'over', elapsed > timer);
    setStyle(this.timeBar, 'width', pct(elapsed / timer));
    const p3 = timer * CHEST_THRESHOLDS.plus3 - elapsed;
    const p2 = timer * CHEST_THRESHOLDS.plus2 - elapsed;
    setText(this.thresholds, `+3 ${p3 > 0 ? clock(p3) : '—'}   +2 ${p2 > 0 ? clock(p2) : '—'}`);
    const req = Math.max(1, m.forcesRequired);
    setStyle(this.forcesFill, 'width', pct(m.forces / req));
    setText(this.forcesText, `Enemy Forces ${Math.min(100, (m.forces / req) * 100).toFixed(1)}%  (${m.forces}/${m.forcesRequired})`);
    setText(this.deaths, `Deaths ${m.deaths}  (-${Math.round(m.deathPenaltyMs / 1000)}s)${m.wipes ? `   Wipes ${m.wipes}` : ''}`);
    const req2 = d?.requiredEncounters ?? [];
    setText(this.bosses, req2.map((id) => `${m.bossesKilled.includes(id) ? '✓' : '○'} ${content.encounter(id)?.name ?? id}`).join('\n'));
    setText(this.hint, m.phase === 'pre' ? 'Move to start the timer' : world.encounter && !world.encounter.result ? `Boss: ${content.encounter(world.encounter.encounterId)?.name ?? ''}` : m.inCombat ? 'In combat' : '');
  }
}
